import { useEffect, useRef, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Play, Pause, Volume2, VolumeX, AlertCircle, X } from "lucide-react";

const formatTime = (s) => {
    if (!isFinite(s) || s < 0) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
};

export default function FloatingAudioPlayer({
    src,
    title,
    episode,
    open,
    autoplayNonce = 0,
    onClose,
}) {
    const audioRef = useRef(null);
    const trackRef = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [current, setCurrent] = useState(0);
    const [duration, setDuration] = useState(0);
    const [muted, setMuted] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;
        const onTime = () => setCurrent(audio.currentTime);
        const onMeta = () => setDuration(audio.duration || 0);
        const onEnd = () => setPlaying(false);
        const onErr = () =>
            setError("Playback not supported in this browser.");
        audio.addEventListener("timeupdate", onTime);
        audio.addEventListener("loadedmetadata", onMeta);
        audio.addEventListener("ended", onEnd);
        audio.addEventListener("error", onErr);
        return () => {
            audio.removeEventListener("timeupdate", onTime);
            audio.removeEventListener("loadedmetadata", onMeta);
            audio.removeEventListener("ended", onEnd);
            audio.removeEventListener("error", onErr);
        };
    }, []);

    const toggle = useCallback(async () => {
        const audio = audioRef.current;
        if (!audio) return;
        setError(null);
        if (audio.paused) {
            try {
                await audio.play();
                setPlaying(true);
            } catch (e) {
                setPlaying(false);
                setError("Couldn’t start playback. Try again.");
            }
        } else {
            audio.pause();
            setPlaying(false);
        }
    }, []);

    // Auto-start playback whenever the parent bumps autoplayNonce.
    useEffect(() => {
        if (!autoplayNonce) return;
        const audio = audioRef.current;
        if (!audio) return;
        setError(null);
        (async () => {
            try {
                await audio.play();
                setPlaying(true);
            } catch (e) {
                setPlaying(false);
                setError("Couldn’t start playback. Try again.");
            }
        })();
    }, [autoplayNonce]);

    const dismiss = useCallback(() => {
        const audio = audioRef.current;
        if (audio && !audio.paused) {
            audio.pause();
            setPlaying(false);
        }
        if (typeof onClose === "function") onClose();
    }, [onClose]);

    const seekTo = useCallback((pct) => {
        const audio = audioRef.current;
        if (!audio || !duration) return;
        const clamped = Math.min(1, Math.max(0, pct));
        audio.currentTime = clamped * duration;
        setCurrent(clamped * duration);
    }, [duration]);

    const onTrackClick = (e) => {
        const track = trackRef.current;
        if (!track) return;
        const rect = track.getBoundingClientRect();
        seekTo((e.clientX - rect.left) / rect.width);
    };

    const onTrackKey = (e) => {
        if (!duration) return;
        const step = 5; // seconds
        if (e.key === "ArrowRight") {
            e.preventDefault();
            seekTo(Math.min(1, (current + step) / duration));
        } else if (e.key === "ArrowLeft") {
            e.preventDefault();
            seekTo(Math.max(0, (current - step) / duration));
        } else if (e.key === "Home") {
            e.preventDefault();
            seekTo(0);
        } else if (e.key === "End") {
            e.preventDefault();
            seekTo(1);
        }
    };

    const toggleMute = () => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.muted = !audio.muted;
        setMuted(audio.muted);
    };

    const progress = duration ? current / duration : 0;

    return (
        <>
            <audio ref={audioRef} src={src} preload="metadata" />
            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        data-testid="audio-player"
                        initial={{ opacity: 0, y: 20, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.96 }}
                        transition={{
                            duration: 0.55,
                            ease: [0.76, 0, 0.24, 1],
                        }}
                        className="w-full max-w-[560px] mx-auto"
                    >
                <div className="flex items-center gap-3 md:gap-5 px-4 md:px-6 py-3 md:py-4 border border-[color:var(--charcoal)]/20 bg-white/60 backdrop-blur-2xl shadow-[0_8px_40px_rgba(13,19,23,0.14)] rounded-full">
                    <button
                        type="button"
                        onClick={toggle}
                        data-testid="audio-toggle"
                        aria-label={playing ? "Pause podcast" : "Play podcast"}
                        aria-pressed={playing}
                        className="flex-shrink-0 h-11 w-11 md:h-12 md:w-12 rounded-full bg-[color:var(--charcoal)] text-white flex items-center justify-center hover:bg-[color:var(--highlight)] transition-colors"
                    >
                        {playing ? (
                            <Pause size={16} strokeWidth={1.8} />
                        ) : (
                            <Play
                                size={16}
                                strokeWidth={1.8}
                                className="translate-x-[1px]"
                            />
                        )}
                    </button>

                    <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-3 mb-1.5">
                            <div className="flex items-baseline gap-2 min-w-0">
                                <span className="font-mono text-[11px] md:text-[12px] uppercase tracking-[0.24em] text-[color:var(--slate)] flex-shrink-0">
                                    {episode}
                                </span>
                                <span className="font-display italic text-sm md:text-base text-[color:var(--charcoal)] truncate">
                                    {error ? (
                                        <span className="flex items-center gap-1.5 text-[color:var(--charcoal)]">
                                            <AlertCircle
                                                size={12}
                                                strokeWidth={1.7}
                                            />
                                            {error}
                                        </span>
                                    ) : (
                                        title
                                    )}
                                </span>
                            </div>
                            <span className="font-mono text-[12px] md:text-xs text-[color:var(--charcoal)] tabular-nums flex-shrink-0">
                                {formatTime(current)} / {formatTime(duration)}
                            </span>
                        </div>
                        <div
                            ref={trackRef}
                            className="progress-track"
                            onClick={onTrackClick}
                            onKeyDown={onTrackKey}
                            role="slider"
                            tabIndex={0}
                            aria-label="Seek within podcast"
                            aria-valuemin={0}
                            aria-valuemax={Math.max(1, Math.floor(duration))}
                            aria-valuenow={Math.floor(current)}
                            aria-valuetext={`${formatTime(current)} of ${formatTime(duration)}`}
                            data-testid="audio-progress"
                            style={{
                                padding: "8px 0",
                                background: "transparent",
                                height: "auto",
                            }}
                        >
                            <div
                                className="relative w-full rounded-full overflow-hidden"
                                style={{
                                    height: 3,
                                    background: "rgba(13,19,23,0.15)",
                                }}
                            >
                                <div
                                    className="absolute inset-y-0 left-0 bg-[color:var(--charcoal)]"
                                    style={{
                                        width: `${progress * 100}%`,
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={toggleMute}
                        data-testid="audio-mute"
                        aria-label={muted ? "Unmute" : "Mute"}
                        aria-pressed={muted}
                        className="flex-shrink-0 hidden sm:flex h-9 w-9 items-center justify-center rounded-full border border-[color:var(--charcoal)]/25 text-[color:var(--charcoal)] hover:bg-[color:var(--charcoal)] hover:text-white transition-colors"
                    >
                        {muted ? (
                            <VolumeX size={14} strokeWidth={1.6} />
                        ) : (
                            <Volume2 size={14} strokeWidth={1.6} />
                        )}
                    </button>
                    <button
                        type="button"
                        onClick={dismiss}
                        data-testid="audio-dismiss"
                        aria-label="Hide player"
                        className="flex-shrink-0 h-9 w-9 flex items-center justify-center rounded-full border border-[color:var(--charcoal)]/25 text-[color:var(--charcoal)] hover:bg-[color:var(--charcoal)] hover:text-white transition-colors"
                    >
                        <X size={13} strokeWidth={1.6} />
                    </button>
                </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
